import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { api } from "../../services/baseUrl";
import {
  Mail,
  ArrowLeft,
  Send,
  AlertCircle,
  RefreshCw,
  Trash2,
} from "lucide-react";

const STATUS_LABELS = {
  nouveau: {
    label: "Nouveau",
    className: "bg-blue-500/20 text-blue-400",
  },
  en_attente: {
    label: "En attente",
    className: "bg-yellow-500/20 text-yellow-400",
  },
  traité: { label: "Traité", className: "bg-green-500/20 text-green-400" },
  lu: { label: "Lu", className: "bg-gray-500/20 text-gray-400" },
};

const MessagingAdmin = ({ isDark }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await api.get("/contact/all-messages");
      const data = response.data?.data || {};
      const list = (data.contact_messages || []).map((m) => ({
        id: m.id,
        name: m.name,
        email: m.email,
        service: m.service || null,
        message: m.message,
        status: m.status || "nouveau",
        created_at: m.created_at,
        updated_at: m.updated_at || m.created_at,
        request_type: m.request_type || null,
        replies: m.replies || [],
      }));
      list.sort(
        (a, b) =>
          new Date(b.updated_at || b.created_at) -
          new Date(a.updated_at || a.created_at),
      );
      setMessages(list);
    } catch (error) {
      toast.error("Erreur lors du chargement des messages");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMessage = async (msg) => {
    setSelected({ ...msg, replies: msg.replies || [] });
    setReplyText("");
    // Recharger le thread complet depuis l'API
    try {
      const refreshed = await api.get(`/contact/messages/${msg.id}`);
      if (refreshed.data?.message) {
        setSelected((prev) => ({
          ...prev,
          replies: refreshed.data.message.replies || [],
          status: refreshed.data.message.status || prev.status,
        }));
      }
    } catch (_) {}
  };

  const handleDelete = async (msg) => {
    if (!window.confirm(`Supprimer le message de ${msg.name} ?`)) return;
    setDeleting(true);
    try {
      await api.delete(`/contact/messages/${msg.id}`);
      toast.success("Message supprimé");
      setMessages((prev) => prev.filter((m) => m.id !== msg.id));
      if (selected?.id === msg.id) setSelected(null);
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) {
      toast.error("La réponse ne peut pas être vide");
      return;
    }
    setSending(true);
    try {
      await api.post(`/contact/messages/${selected.id}/reply`, {
        response: replyText.trim(),
      });
      toast.success("Réponse envoyée !");
      setReplyText("");
      // Recharger le thread pour afficher la nouvelle réponse
      try {
        const refreshed = await api.get(`/contact/messages/${selected.id}`);
        if (refreshed.data?.message) {
          const updated = {
            ...selected,
            replies: refreshed.data.message.replies || [],
            status: "traité",
          };
          setSelected(updated);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === selected.id ? { ...m, status: "traité" } : m,
            ),
          );
        }
      } catch (_) {}
    } catch (error) {
      toast.error("Erreur lors de l'envoi de la réponse");
    } finally {
      setSending(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /* ─── Détail d'un message (style thread comme vue utilisateur) ─── */
  if (selected) {
    const statusInfo =
      STATUS_LABELS[selected.status] || STATUS_LABELS.en_attente;
    return (
      <div className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSelected(null)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              isDark
                ? "bg-white/5 hover:bg-white/10 text-gray-300"
                : "bg-gray-100 hover:bg-gray-200 text-gray-700"
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold ${statusInfo.className}`}
          >
            {statusInfo.label}
          </span>
          <div className="ml-auto flex items-center gap-3">
            <div
              className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}
            >
              <span className="font-medium">{selected.name}</span>
              {" · "}
              <span>{selected.email}</span>
              {selected.service && <span> · {selected.service}</span>}
            </div>
            <button
              onClick={() => handleDelete(selected)}
              disabled={deleting}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-red-400 border border-red-500/30 hover:bg-red-500/10 transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Supprimer
            </button>
          </div>
        </div>

        {/* Fil de conversation */}
        <div
          className={`rounded-xl border overflow-hidden ${isDark ? "bg-[#0b0b0c] border-white/10" : "bg-white border-gray-200"}`}
        >
          {/* Titre de la conversation */}
          <div
            className={`px-4 py-3 border-b flex items-center justify-between ${isDark ? "border-white/6" : "border-gray-100"}`}
          >
            <p className="font-semibold text-sm">
              {selected.service || "Message de contact"}
            </p>
            <span
              className={`text-xs ${isDark ? "text-white/30" : "text-gray-400"}`}
            >
              {formatDate(selected.created_at)}
            </span>
          </div>

          <div className={`px-4 pb-4 space-y-3`}>
            {/* Message initial */}
            <div
              className={`mt-3 p-3 rounded-lg ${isDark ? "bg-white/5" : "bg-gray-50"}`}
            >
              <p
                className={`text-xs font-semibold mb-1 ${isDark ? "text-white/40" : "text-gray-400"}`}
              >
                Message de {selected.name}
              </p>
              <p className="text-sm whitespace-pre-wrap">{selected.message}</p>
              <p
                className={`text-xs mt-2 ${isDark ? "text-white/30" : "text-gray-400"}`}
              >
                {formatDate(selected.created_at)}
              </p>
            </div>

            {/* Replies (fil de discussion) */}
            {selected.replies &&
              selected.replies.length > 0 &&
              selected.replies.map((r) => {
                const isAdmin = !r.id_user || r.name === "Krayne Agency";
                return (
                  <div
                    key={r.id}
                    className={`p-3 rounded-lg border ${
                      isAdmin
                        ? isDark
                          ? "bg-green-500/5 border-green-500/20"
                          : "bg-green-50 border-green-200"
                        : isDark
                          ? "bg-white/5 border-white/10"
                          : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <p
                      className={`text-xs font-semibold mb-1 ${
                        isAdmin
                          ? isDark
                            ? "text-green-400"
                            : "text-green-700"
                          : isDark
                            ? "text-white/50"
                            : "text-gray-500"
                      }`}
                    >
                      {isAdmin ? "Krayne Agency" : r.name}
                    </p>
                    <p className="text-sm whitespace-pre-wrap">{r.message}</p>
                    <p
                      className={`text-xs mt-2 ${isDark ? "text-white/30" : "text-gray-400"}`}
                    >
                      {formatDate(r.created_at)}
                    </p>
                  </div>
                );
              })}

            {/* Aucune réponse encore */}
            {(!selected.replies || selected.replies.length === 0) && (
              <div
                className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                  isDark
                    ? "bg-yellow-500/5 border border-yellow-500/20 text-yellow-400"
                    : "bg-yellow-50 border border-yellow-200 text-yellow-700"
                }`}
              >
                <AlertCircle size={14} />
                Aucune réponse envoyée pour le moment.
              </div>
            )}

            {/* Zone de réponse */}
            <form
              onSubmit={handleReply}
              className={`p-3 rounded-lg border space-y-2 ${isDark ? "bg-white/3 border-white/8" : "bg-gray-50 border-gray-200"}`}
            >
              <p
                className={`text-xs font-semibold ${isDark ? "text-white/50" : "text-gray-500"}`}
              >
                Répondre
              </p>
              <textarea
                rows={3}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Votre réponse..."
                className={`w-full px-3 py-2 rounded-lg border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-500/50 ${
                  isDark
                    ? "bg-white/5 border-white/10 text-white placeholder:text-gray-600"
                    : "bg-white border-gray-200 text-gray-900"
                }`}
              />
              <button
                type="submit"
                disabled={sending || !replyText.trim()}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
                style={{
                  background:
                    "linear-gradient(135deg, #f43f5e 0%, #a855f7 50%, #6366f1 100%)",
                }}
              >
                <Send size={14} />
                {sending ? "Envoi..." : "Envoyer"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  /* ─── Liste des messages ─── */
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="w-6 h-6" />
            Messages reçus
          </h2>
          <p
            className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}
          >
            Consultez et répondez aux messages des clients
          </p>
        </div>
        <button
          onClick={fetchMessages}
          disabled={loading}
          className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-colors ${
            isDark
              ? "bg-white/5 hover:bg-white/10 text-gray-300"
              : "bg-gray-100 hover:bg-gray-200 text-gray-700"
          }`}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Actualiser
        </button>
      </div>

      <div
        className={`rounded-2xl border overflow-hidden ${
          isDark ? "border-white/10" : "border-gray-200"
        }`}
      >
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 mx-auto mb-3 opacity-30 animate-spin" />
            <p className={isDark ? "text-gray-400" : "text-gray-600"}>
              Chargement...
            </p>
          </div>
        ) : messages.length === 0 ? (
          <div className="p-12 text-center">
            <Mail className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p className={isDark ? "text-gray-400" : "text-gray-600"}>
              Aucun message reçu
            </p>
          </div>
        ) : (
          <div
            className={`divide-y ${isDark ? "divide-white/5" : "divide-gray-100"}`}
          >
            {messages.map((msg) => {
              const statusInfo =
                STATUS_LABELS[msg.status] || STATUS_LABELS.en_attente;
              return (
                <div
                  key={msg.id}
                  onClick={() => handleSelectMessage(msg)}
                  className={`flex items-start gap-4 px-5 py-4 cursor-pointer transition-colors ${
                    isDark ? "hover:bg-white/[0.04]" : "hover:bg-gray-50"
                  } ${
                    msg.status === "en_attente"
                      ? "border-l-2 border-yellow-500"
                      : ""
                  }`}
                >
                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-full bg-violet-500/20 flex items-center justify-center shrink-0">
                    <span className="text-sm font-bold text-violet-400">
                      {(msg.name || "?")[0].toUpperCase()}
                    </span>
                  </div>

                  {/* Contenu */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={`font-semibold text-sm ${
                          msg.status === "en_attente"
                            ? isDark
                              ? "text-white"
                              : "text-gray-900"
                            : isDark
                              ? "text-gray-300"
                              : "text-gray-700"
                        }`}
                      >
                        {msg.name}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${statusInfo.className}`}
                      >
                        {statusInfo.label}
                      </span>
                    </div>
                    <div
                      className={`text-xs mt-0.5 ${
                        isDark ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      {msg.email}
                      {msg.service ? ` · ${msg.service}` : ""}
                    </div>
                    <p
                      className={`text-sm mt-1 truncate ${
                        isDark ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      {msg.message}
                    </p>
                  </div>

                  {/* Date + Supprimer */}
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span
                      className={`text-xs ${
                        isDark ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      {new Date(msg.created_at).toLocaleDateString("fr-FR")}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(msg);
                      }}
                      disabled={deleting}
                      className="text-red-400 hover:text-red-300 disabled:opacity-40 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagingAdmin;
